import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICES, SECONDARY_SERVICES } from "../data/content";
import { preselectService } from "../hooks/useLenis";

const SERVICE_ROUTES = {
    heckenschnitt: "/heckenschnitt-celle",
    pflasterreinigung: "/hochdruckreinigung-celle",
    rasenpflege: "/rasenpflege-celle",
    laubentfernung: "/gartenpflege-celle",
};

const ServiceCard = ({ s, index }) => {
    const navigate = useNavigate();
    const route = SERVICE_ROUTES[s.id];
    const go = () => navigate(route);

    return (
        <Reveal delay={index * 0.08} className="h-full">
            <article
                data-testid={`service-card-${s.id}`}
                onClick={go}
                onKeyDown={(e) => {
                    if (e.key === "Enter") go();
                }}
                role="link"
                tabIndex={0}
                aria-label={`Mehr zu ${s.title}`}
                className="group h-full bg-white rounded-3xl overflow-hidden border border-hairline flex flex-col cursor-pointer transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(24,59,43,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-leaf"
            >
                <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                        src={s.image}
                        alt={s.alt}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    />
                    <span className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.2em] bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-forest font-semibold">
                        {s.badge}
                    </span>
                </div>
                <div className="p-7 sm:p-8 flex flex-col flex-1">
                    <div className="flex items-baseline gap-3">
                        <span className="font-mono text-sm text-leaf font-semibold">{s.num}</span>
                        <h3 className="font-display font-semibold tracking-tight text-ink text-xl sm:text-2xl">{s.title}</h3>
                    </div>
                    <p className="mt-3 text-base text-shade leading-relaxed">{s.desc}</p>
                    <ul className="mt-5 space-y-2.5">
                        {s.features.map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm text-shade">
                                <Check size={16} strokeWidth={2.6} className="text-leaf mt-0.5 shrink-0" />
                                {f}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-7 pt-5 border-t border-hairline flex items-center justify-between gap-3">
                        <Link
                            to={route}
                            data-testid={`service-link-${s.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-forest group-hover:text-leaf transition-colors duration-200"
                        >
                            Mehr erfahren
                            <ArrowRight size={15} strokeWidth={2.4} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                        <button
                            data-testid={`service-cta-${s.id}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                preselectService(s.selectValue);
                            }}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-fog hover:text-leaf transition-colors duration-200"
                        >
                            Direkt anfragen
                        </button>
                    </div>
                </div>
            </article>
        </Reveal>
    );
};

export const Services = () => (
    <section id="leistungen" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto" data-testid="services-section">
        <Reveal>
            <Eyebrow num="01" label="Hauptleistungen" />
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <h2 className="font-display font-semibold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.12] max-w-2xl">
                    Vier Leistungen.<br />
                    Ein Anspruch: <span className="text-leaf">sichtbar gepflegt.</span>
                </h2>
                <p className="text-base sm:text-lg text-shade leading-relaxed max-w-md">
                    Wir konzentrieren uns auf das, was den größten Unterschied macht –
                    und machen das richtig.
                </p>
            </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
                <ServiceCard key={s.id} s={s} index={i} />
            ))}
        </div>

        <Reveal delay={0.1} className="mt-20">
            <div className="border-t border-hairline pt-10">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-fog mb-7">
                    Weitere Leistungen für Haus, Garten &amp; Außenbereich
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-6" data-testid="secondary-services">
                    {SECONDARY_SERVICES.map((s) => (
                        <div key={s.title} className="group cursor-default">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-ink group-hover:text-leaf transition-colors duration-200">
                                {s.title}
                                <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200" />
                            </div>
                            <p className="text-sm text-fog mt-1.5 leading-relaxed">{s.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </Reveal>
    </section>
);
